import { useEffect, useState } from 'react';
import { api } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Save, FileText, AlertTriangle, Info, ShieldCheck, Building2, UserX } from 'lucide-react';
import { toast } from 'sonner';

const DEFAULT_RATES = [
    { profile: 'fisica', label: 'Persona Física (con RFC)', isr_pct: 2.5, iva_pct: 8.0 },
    { profile: 'moral', label: 'Persona Moral / Empresa (con RFC)', isr_pct: 2.5, iva_pct: 8.0 },
    { profile: 'sin_rfc', label: 'Sin RFC registrado (tasa de penalización)', isr_pct: 20.0, iva_pct: 16.0 },
];

const profileIcons = { fisica: ShieldCheck, moral: Building2, sin_rfc: UserX };
const profileColors = {
    fisica: 'border-emerald-300 bg-emerald-50/50',
    moral: 'border-blue-300 bg-blue-50/50',
    sin_rfc: 'border-red-300 bg-red-50/50',
};

export default function AdminImpuestos() {
    const [cfg, setCfg] = useState(null);
    const [saving, setSaving] = useState(false);

    const load = async () => {
        const { data } = await api.get('/admin/taxes').catch(() => ({ data: null }));
        if (data) {
            setCfg({ ...data, rates: data.rates?.length ? data.rates : DEFAULT_RATES });
        } else {
            setCfg({ rates: DEFAULT_RATES, guanajuato_extra_pct: 2.5, notes: '' });
        }
    };

    useEffect(() => { load(); }, []);

    const setRate = (i, field, val) => {
        setCfg(prev => ({
            ...prev,
            rates: prev.rates.map((r, j) => j === i ? { ...r, [field]: val } : r),
        }));
    };

    const save = async () => {
        setSaving(true);
        try {
            await api.put('/admin/taxes', cfg);
            toast.success('Configuración de impuestos guardada');
        } catch (e) { toast.error(e?.response?.data?.detail || 'Error'); }
        finally { setSaving(false); }
    };

    if (!cfg) return <div className="text-sm text-muted-foreground">Cargando…</div>;

    return (
        <div className="space-y-6 max-w-4xl">
            <div className="flex items-center justify-between">
                <div>
                    <h1 className="text-2xl font-semibold tracking-tight">Impuestos</h1>
                    <p className="text-sm text-muted-foreground">Configura las tasas de retención fiscal (ISR e IVA) según la Ley de Plataformas Digitales del SAT.</p>
                </div>
                <Button onClick={save} disabled={saving} className="bg-red-600 hover:bg-red-700">
                    <Save className="h-4 w-4 mr-2" />{saving ? 'Guardando…' : 'Guardar cambios'}
                </Button>
            </div>

            {/* Legal basis banner */}
            <div className="rounded-xl border bg-amber-50/60 border-amber-200 p-4 flex items-start gap-3">
                <Info className="h-5 w-5 text-amber-600 shrink-0 mt-0.5" />
                <div className="text-sm text-amber-800">
                    <span className="font-semibold">Marco legal:</span> El artículo 18-J de la LISR y los artículos 1-A Bis del IVA obligan a las plataformas digitales a retener ISR e IVA a sus vendedores.
                    Las tasas vigentes (reforma fiscal) son ISR 2.5% + IVA 8% para contribuyentes con RFC; y penalización de ISR 20% + IVA 16% para vendedores sin RFC.
                    <strong className="block mt-1">Modificar estas tasas puede tener implicaciones legales. Consulta a tu área fiscal antes de cambiar los valores.</strong>
                </div>
            </div>

            {/* Tax rates table */}
            <div className="space-y-4">
                <h2 className="font-medium flex items-center gap-2"><FileText className="h-4 w-4 text-red-600" />Tasas de retención por régimen fiscal</h2>
                {cfg.rates.map((rate, i) => {
                    const Icon = profileIcons[rate.profile] || ShieldCheck;
                    return (
                        <div key={rate.profile} className={`rounded-xl border p-5 space-y-4 ${profileColors[rate.profile] || ''}`}>
                            <div className="flex items-center gap-3">
                                <div className={`h-9 w-9 rounded-full flex items-center justify-center ${rate.profile === 'sin_rfc' ? 'bg-red-100' : rate.profile === 'moral' ? 'bg-blue-100' : 'bg-emerald-100'}`}>
                                    <Icon className={`h-5 w-5 ${rate.profile === 'sin_rfc' ? 'text-red-600' : rate.profile === 'moral' ? 'text-blue-600' : 'text-emerald-600'}`} />
                                </div>
                                <div>
                                    <Input value={rate.label} onChange={e => setRate(i, 'label', e.target.value)} className="font-semibold h-8 text-sm border-transparent bg-transparent hover:border-input focus:border-input px-0 focus:px-3 w-80" />
                                    <div className="text-xs text-muted-foreground mt-0.5">Perfil: <code className="bg-white/70 px-1 rounded">{rate.profile}</code></div>
                                </div>
                                {rate.profile === 'sin_rfc' && (
                                    <Badge variant="destructive" className="ml-auto">Penalización máxima</Badge>
                                )}
                            </div>
                            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                                <div>
                                    <Label className="text-xs">ISR (%)</Label>
                                    <Input type="number" min="0" max="100" step="0.5"
                                        value={rate.isr_pct}
                                        onChange={e => setRate(i, 'isr_pct', parseFloat(e.target.value) || 0)}
                                        className="mt-1 h-8 text-sm" />
                                </div>
                                <div>
                                    <Label className="text-xs">IVA (%)</Label>
                                    <Input type="number" min="0" max="100" step="0.5"
                                        value={rate.iva_pct}
                                        onChange={e => setRate(i, 'iva_pct', parseFloat(e.target.value) || 0)}
                                        className="mt-1 h-8 text-sm" />
                                </div>
                                <div className="col-span-2 flex items-end">
                                    <div className="w-full rounded-lg bg-white/70 border px-3 py-2 text-sm">
                                        <span className="text-muted-foreground">Retención total:</span>
                                        <span className="ml-2 font-bold text-slate-900">{(rate.isr_pct + rate.iva_pct).toFixed(1)}%</span>
                                        <span className="ml-2 text-xs text-muted-foreground">= ISR {rate.isr_pct}% + IVA {rate.iva_pct}%</span>
                                    </div>
                                </div>
                            </div>
                        </div>
                    );
                })}
            </div>

            {/* Guanajuato extra */}
            <div className="rounded-xl border bg-card p-5 space-y-3">
                <div className="flex items-center gap-2">
                    <AlertTriangle className="h-4 w-4 text-amber-500" />
                    <h2 className="font-medium">Impuesto Cedular SATEG — Guanajuato</h2>
                </div>
                <p className="text-xs text-muted-foreground">Se aplica un descuento adicional a vendedores con dirección registrada en el estado de Guanajuato.</p>
                <div className="flex items-center gap-3">
                    <div className="w-40">
                        <Label className="text-xs">Porcentaje adicional (%)</Label>
                        <Input type="number" min="0" max="50" step="0.5"
                            value={cfg.guanajuato_extra_pct}
                            onChange={e => setCfg(prev => ({ ...prev, guanajuato_extra_pct: parseFloat(e.target.value) || 0 }))}
                            className="mt-1 h-8 text-sm" />
                    </div>
                </div>
            </div>

            {/* Notes */}
            <div className="rounded-xl border bg-card p-5 space-y-3">
                <h2 className="font-medium">Notas internas</h2>
                <Textarea placeholder="Notas fiscales, referencias legales, historial de cambios…"
                    value={cfg.notes || ''}
                    onChange={e => setCfg(prev => ({ ...prev, notes: e.target.value }))}
                    rows={3} />
            </div>

            {/* Summary table */}
            <div className="rounded-xl border bg-card overflow-hidden">
                <div className="px-5 py-3 border-b font-medium text-sm">Resumen de retenciones actuales</div>
                <table className="w-full text-sm">
                    <thead>
                        <tr className="border-b bg-neutral-50 text-xs text-muted-foreground">
                            <th className="text-left px-5 py-2">Régimen</th>
                            <th className="text-right px-4 py-2">ISR</th>
                            <th className="text-right px-4 py-2">IVA</th>
                            <th className="text-right px-4 py-2">Total retenido</th>
                            <th className="text-right px-5 py-2">Vendedor recibe</th>
                        </tr>
                    </thead>
                    <tbody>
                        {cfg.rates.map(r => (
                            <tr key={r.profile} className="border-b last:border-0">
                                <td className="px-5 py-2.5 font-medium">{r.label}</td>
                                <td className="px-4 py-2.5 text-right tabular-nums">{r.isr_pct}%</td>
                                <td className="px-4 py-2.5 text-right tabular-nums">{r.iva_pct}%</td>
                                <td className="px-4 py-2.5 text-right tabular-nums font-semibold text-red-600">{(r.isr_pct + r.iva_pct).toFixed(1)}%</td>
                                <td className="px-5 py-2.5 text-right tabular-nums font-semibold text-emerald-700">{(100 - r.isr_pct - r.iva_pct).toFixed(1)}%</td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>

            <div className="flex justify-end">
                <Button onClick={save} disabled={saving} className="bg-red-600 hover:bg-red-700">
                    <Save className="h-4 w-4 mr-2" />{saving ? 'Guardando…' : 'Guardar configuración'}
                </Button>
            </div>
        </div>
    );
}
